import API from "../Network/API"
import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import AuthLayout, { PasswordInput, Spinner } from "./AuthLayout";

const EMAIL_RE = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w\w+)+$/;

const redirectForRole = (role) => {
  if (role === "teacher") {
    window.location.href = '/dashboard/tutor';
  } else if (role === "student") {
    window.location.href = '/home/student';
  } else {
    window.location.href = '/';
  }
};

export default function Login() {
  const { t } = useTranslation();
  const { user, accessToken } = useSelector((state) => state.authReducer);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // already signed in — send them to their dashboard
  useEffect(() => {
    if (accessToken) {
      redirectForRole(user?.role);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!EMAIL_RE.test(email)) {
      toast.error(t('login.email_not_correct'));
      return;
    }
    setLoading(true);
    setErrors({});
    API.post('/v1/oauth/login', { email, password }).then(response => {
      setLoading(false);
      if (response.data.success === true) {
        localStorage.setItem(
          'xller_auth',
          JSON.stringify({
            user: response?.data?.data?.user,
            accessToken: response?.data?.data?.access_token,
          })
        )
        localStorage.setItem("xller_token", response?.data?.data?.access_token)
        toast.success(response.data.message, { duration: 3000 })

        // finish a checkout that was interrupted by the login
        if (localStorage.getItem('data_checkout') != null) {
          window.location.href = '/checkout';
          return;
        }
        redirectForRole(response?.data?.data?.user?.role);
      }
    }).catch((error) => {
      setLoading(false);
      if (error.response?.data?.data) {
        setErrors(error.response.data.data);
      } else if (error.response?.data) {
        setErrors(error.response.data);
      } else {
        toast.error(t("auth.something_wrong"));
      }
    });
  }

  return (
    <AuthLayout
      title={t("auth.login_title")}
      subtitle={t("auth.login_subtitle")}
      image="/assets/img/teach/teach-global.jpg"
      accent="purple"
      panelTitle={t("auth.panel_login_title")}
      panelPoints={[t("auth.panel_login_point_1"), t("auth.panel_login_point_2"), t("auth.panel_login_point_3")]}
      footer={
        <>
          <div className="auth-switch auth-switch--split">
            <span>{t("auth.no_account")}</span>
            <div className="auth-switch-links">
              <Link to="/signup-student">{t('login.sign_up_as_student')}</Link>
              <Link to="/signup-tutor">{t('login.sign_up_as_tutor')}</Link>
            </div>
          </div>
          <p className="auth-legal">
            <Trans i18nKey="auth.login_terms" components={{ termsLink: <Link to="/terms" />, privacyLink: <Link to="/privacy-policy" /> }} />
          </p>
        </>
      }
    >
      <form className="auth-form" onSubmit={handleSubmit}>
        {errors.message && (
          <div className="auth-alert" role="alert">
            <i className="fa-light fa-circle-exclamation" /> {errors.message}
          </div>
        )}
        <div className="auth-field">
          <label htmlFor="login-email">{t('login.email')}</label>
          <input
            id="login-email"
            type="email"
            required
            autoComplete="email"
            className="auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('login.your_email')}
          />
        </div>
        <div className="auth-field">
          <div className="auth-label-row">
            <label htmlFor="login-password">{t('login.password')}</label>
            <Link to="/forgot-password" className="auth-forgot">{t('login.forgot_your_password')}</Link>
          </div>
          <PasswordInput
            id="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('login.your_password')}
            autoComplete="current-password"
            invalid={Boolean(errors.message)}
          />
        </div>
        <button type="submit" className="auth-submit" disabled={isLoading}>
          {isLoading ? <Spinner /> : null}
          {t('login.login')}
        </button>
      </form>
    </AuthLayout>
  );
}
